import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import ExtensionRenderer from '../../ui/ExtensionRenderer';
import { overflowShadow, WidthConsumer } from '@atlaskit/editor-common/ui';
import { calcBreakoutWidth } from '@atlaskit/editor-common/utils';
import { RendererCssClassName } from '../../consts';
var viewportSizes = ['small', 'medium', 'default', 'large', 'xlarge'];
// Mirrors sizes from https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/platform/packages/forge/xen-editor-provider/src/render/renderers/ForgeUIExtension.tsx
var macroHeights = {
  small: '112px',
  medium: '262px',
  default: '262px',
  large: '524px',
  xlarge: '1048px'
};
var getViewportSize = function getViewportSize(extensionId, extensionViewportSizes) {
  if (!Array.isArray(extensionViewportSizes) || !extensionId) {
    return;
  }
  var extension = extensionViewportSizes.find(function (extension) {
    return extension.extensionId === extensionId;
  });
  if (extension) {
    var viewportSize = extension.viewportSize && viewportSizes.includes(extension.viewportSize) ? extension.viewportSize : 'default';
    return macroHeights[viewportSize];
  }
};
export var renderExtension = function renderExtension(content, layout) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var removeOverflow = arguments.length > 3 ? arguments[3] : undefined;
  var extensionId = arguments.length > 4 ? arguments[4] : undefined;
  var extensionViewportSizes = arguments.length > 5 ? arguments[5] : undefined;
  var nodeHeight = arguments.length > 6 ? arguments[6] : undefined;
  var localId = arguments.length > 7 ? arguments[7] : undefined;
  var overflowContainerClass = !removeOverflow ? RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER : '';

  // by default, we assume the extension is at top level, (direct child of doc node)
  var _ref = options || {},
    _ref$isTopLevel = _ref.isTopLevel,
    isTopLevel = _ref$isTopLevel === void 0 ? true : _ref$isTopLevel;
  var centerAlignClass = isTopLevel && ['wide', 'full-width'].includes(layout) ? RendererCssClassName.EXTENSION_CENTER_ALIGN : '';
  /**
   * To reduce cumulative layout shift, we check installed manifest values (viewportSize) for Forge and extension node parameters
   * for Connect (legacy). As Connect is being phased out, we want Forge to also start to store its expected height
   * in node parameters, especially for dynamic content macros. LegacyMacroStyledElements implements logic similar to here
   * as the extension handler in CFE for legacy macros and Connect.
   */
  var viewportSize = getViewportSize(extensionId, extensionViewportSizes);
  var extensionHeight = nodeHeight || viewportSize;
  return /*#__PURE__*/React.createElement(WidthConsumer, null, function (_ref2) {
    var width = _ref2.width;
    return /*#__PURE__*/React.createElement("div", {
      ref: options.handleRef
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
      ,
      className: "".concat(RendererCssClassName.EXTENSION, " ").concat(options.shadowClassNames, " ").concat(centerAlignClass),
      style: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
        width: isTopLevel ? calcBreakoutWidth(layout, width) : '100%',
        // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
        minHeight: "".concat(extensionHeight, "px")
      },
      "data-layout": layout,
      "data-local-id": localId
    }, /*#__PURE__*/React.createElement("div", {
      className: overflowContainerClass
    }, content));
  });
};
var Extension = function Extension(props) {
  var text = props.text,
    _props$layout = props.layout,
    layout = _props$layout === void 0 ? 'default' : _props$layout,
    handleRef = props.handleRef,
    shadowClassNames = props.shadowClassNames,
    _props$path = props.path,
    path = _props$path === void 0 ? [] : _props$path,
    extensionViewportSizes = props.extensionViewportSizes,
    parameters = props.parameters,
    nodeHeight = props.nodeHeight,
    localId = props.localId;
  return /*#__PURE__*/React.createElement(ExtensionRenderer
  // Ignored via go/ees005
  // eslint-disable-next-line react/jsx-props-no-spreading
  , _extends({}, props, {
    type: "extension"
  }), function (_ref3) {
    var result = _ref3.result;
    try {
      // Return the result directly if it's a valid JSX.Element
      if (result && /*#__PURE__*/React.isValidElement(result)) {
        return renderExtension(result, layout, {
          isTopLevel: path.length < 1,
          handleRef: handleRef,
          shadowClassNames: shadowClassNames
        }, undefined, parameters === null || parameters === void 0 ? void 0 : parameters.extensionId, extensionViewportSizes, nodeHeight, localId);
      }
    } catch (e) {
      /** We don't want this error to block renderer */
      /** We keep rendering the default content */
    }
    // Always return default content if anything goes wrong
    return renderExtension(text || 'extension', layout, {
      isTopLevel: path.length < 1,
      handleRef: handleRef,
      shadowClassNames: shadowClassNames
    }, undefined, parameters === null || parameters === void 0 ? void 0 : parameters.extensionId, extensionViewportSizes, nodeHeight, localId);
  });
};
export default overflowShadow(Extension, {
  overflowSelector: ".".concat(RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER)
});